import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { mdiSealVariant, mdiCogOutline } from '@mdi/js'
import { AppIcon } from '../ui/icon'
import { Button } from '../ui/button'
import { Textarea } from '../ui/textarea'
import { Label } from '../ui/label'
import { Badge } from '../ui/badge'
import { useFormatters } from '../../lib/format'
import { InterventionSectionTitle } from '../interventions/InterventionDocumentsSection'
import {
    useInterventionValidation,
    useValidateWork,
    type EquipmentDecision,
    type PendingEquipment,
    type RecoverableDecision,
    type ValidationChargeLine,
} from '../../hooks/useWorkOrder'
import { percent, recoverableKey } from '../../hooks/useRecoverableCharges'

type ChargeChoice = 'accept' | 'yes' | 'no' | 'leave'

/** The proposal is preselected; a qualified line or one without proposal is left as is. */
const defaultChoice = (l: ValidationChargeLine): ChargeChoice =>
    l.recoverable == null && l.recoverable_proposal?.status === 'proposed' && l.recoverable_proposal.rule ? 'accept' : 'leave'

/**
 * InterventionValidationSection is the syndic's half of the loop.
 *
 * Approving is the pivot event of the whole spec: it authorizes payment
 * (finance phase) and stops the contractual response clock on a *validated*
 * performed_at rather than on the provider's own word. The same gesture settles
 * the registry — each equipment the provider declared is confirmed, merged into
 * an existing one, or rejected.
 *
 * Equipment left undecided stays a candidate. Approving the work is never taken
 * as consent to admit unreviewed equipment into the registry.
 */
export function InterventionValidationSection({
    org,
    interventionUid,
    workSubmittedAt,
}: {
    org: string
    interventionUid: string
    workSubmittedAt?: string | null
}) {
    const { t } = useTranslation('workorders')
    const { formatDate } = useFormatters()
    const { data } = useInterventionValidation(org, interventionUid, !!workSubmittedAt)
    const validate = useValidateWork(org, interventionUid)

    const [disputing, setDisputing] = useState(false)
    const [reason, setReason] = useState('')
    const [decisions, setDecisions] = useState<Record<string, EquipmentDecision>>({})
    const [err, setErr] = useState<string | null>(null)
    const [charges, setCharges] = useState<Record<string, ChargeChoice>>({})

    // Nothing to validate until the provider has reported the work — showing an
    // empty block would only add noise to interventions handled in-house.
    if (!workSubmittedAt || !data) return null

    const fmt = (s?: string | null) => (s ? formatDate(s, { dateStyle: 'long', timeStyle: 'short' }) : '')

    const setDecision = (uid: string, action: EquipmentDecision['action'], mergeInto?: string) =>
        setDecisions((prev) => ({
            ...prev,
            [uid]: { asset_uid: uid, action, merge_into_uid: mergeInto },
        }))

    const chargeLines = data.charge_lines ?? []
    const choiceOf = (l: ValidationChargeLine) => charges[l.expense_uid] ?? defaultChoice(l)
    const recoverablePayload = (): RecoverableDecision[] | undefined => {
        const out: RecoverableDecision[] = []
        for (const l of chargeLines) {
            const c = choiceOf(l)
            const p = l.recoverable_proposal
            if (c === 'accept' && p?.rule && p.recoverable != null) {
                out.push({ expense_uid: l.expense_uid, recoverable: p.recoverable, recoverable_ratio: p.recoverable_ratio ?? null, rule_uid: p.rule.uid })
            } else if (c === 'yes' || c === 'no') {
                out.push({ expense_uid: l.expense_uid, recoverable: c === 'yes' })
            }
        }
        return out.length ? out : undefined
    }

    const equipmentPayload = (): EquipmentDecision[] | undefined => {
        const list = Object.values(decisions)
        return list.length ? list : undefined
    }

    const run = (verdict: 'approved' | 'disputed') => {
        setErr(null)
        if (verdict === 'disputed' && !reason.trim()) {
            setErr(t('validation.errors.reason_required'))
            return
        }
        const equipment = equipmentPayload()
        const bad = equipment?.find((d) => d.action === 'merge' && !d.merge_into_uid)
        if (bad) {
            setErr(t('validation.errors.merge_target_required'))
            return
        }
        validate.mutate(
            {
                verdict,
                reason: verdict === 'disputed' ? reason : undefined,
                equipment,
                recoverable: verdict === 'approved' ? recoverablePayload() : undefined,
            },
            {
                onSuccess: () => {
                    setDisputing(false)
                    setReason('')
                    setDecisions({})
                    setCharges({})
                },
                onError: () => setErr(t('validation.errors.generic')),
            },
        )
    }

    return (
        <div data-testid="intervention-validation">
            <InterventionSectionTitle icon={mdiSealVariant} testid="intervention-validation-title">
                {t('validation.title')}
            </InterventionSectionTitle>

            <div className="space-y-4 pt-2">
                <p className="text-sm text-muted-foreground" data-testid="validation-submitted-on">
                    {t('validation.submitted_on', { date: fmt(data.work_submitted_at) })}
                </p>

                {data.report_note ? (
                    <div className="rounded-md border bg-muted/30 p-3 text-sm" data-testid="validation-report-note">
                        <p className="mb-1 font-medium">{t('validation.report_note')}</p>
                        <p className="whitespace-pre-wrap text-muted-foreground">{data.report_note}</p>
                    </div>
                ) : null}

                {data.validation_status === 'approved' ? (
                    <Badge variant="default" data-testid="validation-approved-badge">
                        {t('validation.approved_on', { date: fmt(data.validated_at) })}
                    </Badge>
                ) : null}
                {data.validation_status === 'disputed' ? (
                    <Badge variant="destructive" data-testid="validation-disputed-badge">
                        {t('validation.disputed', { reason: data.dispute_reason })}
                    </Badge>
                ) : null}

                {data.touched_equipment.length > 0 ? (
                    <div data-testid="validation-touched">
                        <p className="mb-1 text-sm font-medium">{t('validation.touched_title')}</p>
                        <ul className="space-y-0.5 text-sm text-muted-foreground">
                            {data.touched_equipment.map((e) => (
                                <li key={e.uid}>
                                    {e.name || e.type}
                                    {e.pending ? ' — ' + t('portal.equipment_pending_short') : ''}
                                </li>
                            ))}
                        </ul>
                    </div>
                ) : null}

                {data.pending_equipment.length > 0 ? (
                    <div className="space-y-3" data-testid="validation-pending-equipment">
                        <div>
                            <p className="flex items-center gap-1.5 text-sm font-medium">
                                <AppIcon path={mdiCogOutline} size="sm" />
                                {t('validation.pending_title')}
                            </p>
                            <p className="text-xs text-muted-foreground">{t('validation.pending_hint')}</p>
                        </div>
                        {data.pending_equipment.map((eq) => (
                            <PendingEquipmentRow
                                key={eq.uid}
                                equipment={eq}
                                decision={decisions[eq.uid]}
                                onDecide={setDecision}
                            />
                        ))}
                    </div>
                ) : null}

                {chargeLines.length > 0 ? (
                    <div className="space-y-2" data-testid="validation-charge-lines">
                        <div>
                            <p className="text-sm font-medium">{t('validation.charges_title')}</p>
                            {data.validation_status !== 'approved' && (
                                <p className="text-xs text-muted-foreground">{t('validation.charges_hint')}</p>
                            )}
                        </div>
                        {chargeLines.map((l) => (
                            <ChargeLineRow
                                key={l.expense_uid}
                                line={l}
                                choice={choiceOf(l)}
                                editable={data.validation_status !== 'approved'}
                                onChoose={(c) => setCharges((prev) => ({ ...prev, [l.expense_uid]: c }))}
                            />
                        ))}
                    </div>
                ) : null}

                {data.validation_status !== 'approved' ? (
                    <div className="space-y-3">
                        {!disputing ? (
                            <>
                                <p className="text-xs text-muted-foreground">{t('validation.approve_hint')}</p>
                                <div className="flex flex-col gap-2 sm:flex-row">
                                    <Button
                                        onClick={() => run('approved')}
                                        disabled={validate.isPending}
                                        data-testid="validation-approve-btn"
                                    >
                                        {t('validation.approve')}
                                    </Button>
                                    <Button
                                        variant="outline"
                                        onClick={() => setDisputing(true)}
                                        data-testid="validation-dispute-open-btn"
                                    >
                                        {t('validation.dispute')}
                                    </Button>
                                </div>
                            </>
                        ) : (
                            <div className="space-y-2">
                                <Label htmlFor="validation-reason">{t('validation.dispute_reason_label')}</Label>
                                <Textarea
                                    id="validation-reason"
                                    rows={3}
                                    value={reason}
                                    onChange={(e) => setReason(e.target.value)}
                                    data-testid="validation-reason-input"
                                />
                                <p className="text-xs text-muted-foreground">
                                    {t('validation.dispute_reason_hint')}
                                </p>
                                <div className="flex gap-2">
                                    <Button
                                        variant="destructive"
                                        onClick={() => run('disputed')}
                                        disabled={validate.isPending}
                                        data-testid="validation-dispute-btn"
                                    >
                                        {t('validation.confirm_dispute')}
                                    </Button>
                                    <Button
                                        variant="ghost"
                                        onClick={() => setDisputing(false)}
                                        data-testid="validation-dispute-cancel-btn"
                                    >
                                        {t('validation.cancel')}
                                    </Button>
                                </div>
                            </div>
                        )}
                    </div>
                ) : null}

                {err ? (
                    <p className="text-sm text-destructive" data-testid="validation-error">
                        {err}
                    </p>
                ) : null}
            </div>
        </div>
    )
}

/** One charge line of the intervention, its proposal and the person's choice. */
function ChargeLineRow({
    line,
    choice,
    editable,
    onChoose,
}: {
    line: ValidationChargeLine
    choice: ChargeChoice
    editable: boolean
    onChoose: (c: ChargeChoice) => void
}) {
    const { t } = useTranslation('workorders')
    const { t: te } = useTranslation('expenses')
    const { formatMoney } = useFormatters()
    const p = line.recoverable_proposal
    const proposed = p?.status === 'proposed' && p.rule
    const phrase = (rec: boolean | null | undefined, ratio?: number | null) =>
        te(`recoverable.proposal_${recoverableKey(rec, ratio) === 'unqualified' ? 'no' : recoverableKey(rec, ratio)}`, { percent: percent(ratio) })

    return (
        <div className="flex flex-wrap items-center gap-3 rounded-md border p-3" data-testid={`validation-charge-line-${line.expense_uid}`}>
            <div className="min-w-0 flex-1">
                <p className="text-sm font-medium">{line.label}</p>
                <p className="text-xs text-muted-foreground">
                    {formatMoney(line.amount, line.currency)}
                    {proposed ? ` · ${te('recoverable.proposal_label')} ${phrase(p.recoverable, p.recoverable_ratio)}${p.rule?.legal_basis ? ` (${p.rule.legal_basis})` : ''}` : ''}
                    {!proposed && p ? ` · ${te(`recoverable.status_${p.status}`, { jurisdiction: p.jurisdiction || '?' })}` : ''}
                </p>
                {line.recoverable != null && (
                    <p className="text-xs" data-testid={`validation-charge-qualified-${line.expense_uid}`}>
                        {t('validation.charge_qualified', { value: phrase(line.recoverable, line.recoverable_ratio) })}
                    </p>
                )}
            </div>
            {editable && (
                <select
                    className="h-9 rounded-md border border-input bg-background px-3 text-sm"
                    value={choice}
                    onChange={(e) => onChoose(e.target.value as ChargeChoice)}
                    data-testid={`validation-recoverable-${line.expense_uid}`}
                >
                    {proposed && (
                        <option value="accept">{t('validation.charge_accept', { value: phrase(p.recoverable, p.recoverable_ratio) })}</option>
                    )}
                    <option value="yes">{t('validation.charge_yes')}</option>
                    <option value="no">{t('validation.charge_no')}</option>
                    <option value="leave">{t('validation.charge_leave')}</option>
                </select>
            )}
        </div>
    )
}

/**
 * PendingEquipmentRow shows one candidate together with the registry equipment
 * it might duplicate — the anti-duplicate guard surfaced as a question ("is it
 * one of these?") rather than left for the manager to notice.
 */
function PendingEquipmentRow({
    equipment,
    decision,
    onDecide,
}: {
    equipment: PendingEquipment
    decision?: EquipmentDecision
    onDecide: (uid: string, action: EquipmentDecision['action'], mergeInto?: string) => void
}) {
    const { t } = useTranslation('workorders')

    return (
        <div className="space-y-2 rounded-md border p-3" data-testid={`validation-candidate-${equipment.uid}`}>
            <div>
                <p className="text-sm font-medium">{equipment.name}</p>
                {equipment.type_name ? (
                    <p className="text-xs text-muted-foreground">{equipment.type_name}</p>
                ) : null}
            </div>

            <div className="flex flex-wrap gap-2">
                <Button
                    size="sm"
                    variant={decision?.action === 'confirm' ? 'default' : 'outline'}
                    onClick={() => onDecide(equipment.uid, 'confirm')}
                    data-testid={`validation-confirm-${equipment.uid}`}
                >
                    {t('validation.action_confirm')}
                </Button>
                <Button
                    size="sm"
                    variant={decision?.action === 'reject' ? 'destructive' : 'outline'}
                    onClick={() => onDecide(equipment.uid, 'reject')}
                    data-testid={`validation-reject-${equipment.uid}`}
                >
                    {t('validation.action_reject')}
                </Button>
            </div>

            {equipment.similar.length > 0 ? (
                <div className="space-y-1">
                    <p className="text-xs text-muted-foreground">{t('validation.similar_label')}</p>
                    <select
                        className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
                        value={decision?.action === 'merge' ? (decision.merge_into_uid ?? '') : ''}
                        onChange={(e) =>
                            e.target.value
                                ? onDecide(equipment.uid, 'merge', e.target.value)
                                : onDecide(equipment.uid, 'confirm')
                        }
                        data-testid={`validation-merge-select-${equipment.uid}`}
                    >
                        <option value="">{t('validation.merge_placeholder')}</option>
                        {equipment.similar.map((s) => (
                            <option key={s.uid} value={s.uid}>
                                {[s.name || s.type_name, s.location].filter(Boolean).join(' — ')}
                            </option>
                        ))}
                    </select>
                </div>
            ) : null}
        </div>
    )
}
